import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  message,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/vi_VN";
import { prescriptionService } from "../../services";
import { Prescription } from "../../types";
import { formatDate } from "../../utils/format";
import PrescriptionDetailModal from "../../components/PrescriptionDetailModal";
import "./NationalPrescriptionPage.css";

const NationalPrescriptionPage: React.FC = () => {
  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPrescriptionCode, setSelectedPrescriptionCode] = useState<
    string | null
  >(null);

  const [filters, setFilters] = useState({
    code: "",
    patientName: "",
    doctorName: "",
    startDate: "",
    endDate: "",
  });

  const { data: prescriptions, isLoading } = useQuery({
    queryKey: ["national-prescriptions", filters],
    queryFn: () => prescriptionService.getNationalPrescriptions(filters),
  });

  const onSearch = (values: any) => {
    setFilters({
      code: values.code || "",
      patientName: values.patientName || "",
      doctorName: values.doctorName || "",
      startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
      endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : "",
    });
  };

  const handleViewDetail = (code: string) => {
    setSelectedPrescriptionCode(code);
    setDetailModalVisible(true);
  };

  const handleDownload = (code: string) => {
    message.success(`Đang tải đơn thuốc ${code}`);
    // TODO: Implement download functionality
  };

  const handleExportReport = () => {
    message.success("Đang xuất báo cáo...");
    // TODO: Implement export report functionality
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    const date = dayjs(dateString);
    return date.format("DD/MM/YYYY HH:mm");
  };

  const columns = [
    {
      title: "STT",
      dataIndex: "index",
      key: "index",
      width: 60,
    },
    {
      title: "Mã đơn thuốc",
      dataIndex: "code",
      key: "code",
      render: (text: string, record: Prescription) => (
        <Button
          type="link"
          onClick={() => handleViewDetail(text)}
          style={{ padding: 0, color: "#385da4" }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Bác sĩ",
      dataIndex: "doctorName",
      key: "doctorName",
    },
    {
      title: "Tên bệnh nhân",
      dataIndex: "patientName",
      key: "patientName",
    },
    {
      title: "Chẩn đoán",
      dataIndex: "diagnosis",
      key: "diagnosis",
    },
    {
      title: "Ngày tạo đơn",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Ngày gửi đơn",
      dataIndex: "sentDate",
      key: "sentDate",
      render: (date: string) => (date ? formatDateTime(date) : "-"),
    },
    {
      title: "Xem đơn thuốc",
      key: "view",
      width: 150,
      render: (_: any, record: Prescription) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.code)}
          style={{ color: "#385da4" }}
        >
          Xem chi tiết
        </Button>
      ),
    },
    {
      title: "Tải về",
      key: "download",
      width: 100,
      render: (_: any, record: Prescription) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.code)}
          style={{ color: "#385da4" }}
        />
      ),
    },
  ];

  const tableData = prescriptions?.map((p, index) => ({
    ...p,
    index: index + 1,
    key: p.id,
  }));

  return (
    <div className="national-prescription-page">
      <h1 className="page-title">
        Quản lý đơn thuốc ngày {formatDate(dayjs().format("YYYY-MM-DD"))}
      </h1>

      <Form
        form={form}
        layout="inline"
        onFinish={onSearch}
        className="search-form"
      >
        <Form.Item name="code">
          <Input placeholder="Mã đơn thuốc" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="patientName">
          <Input placeholder="Tên bệnh nhân" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="doctorName">
          <Input placeholder="Bác sĩ" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="startDate">
          <DatePicker
            placeholder="Từ ngày"
            format="DD/MM/YYYY"
            locale={locale}
          />
        </Form.Item>
        <Form.Item name="endDate">
          <DatePicker
            placeholder="Đến ngày"
            format="DD/MM/YYYY"
            locale={locale}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              Tìm kiếm
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportReport}
            >
              Xuất báo cáo
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} đơn thuốc`,
        }}
      />

      <PrescriptionDetailModal
        visible={detailModalVisible}
        code={selectedPrescriptionCode}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedPrescriptionCode(null);
        }}
      />
    </div>
  );
};

export default NationalPrescriptionPage;
